import React, { useState, useEffect } from "react";
import Card from "@mui/material/Card";
import Divider from "@mui/material/Divider";
import TextField from "@mui/material/TextField";
import Button from "@mui/material/Button";
import SoftBox from "components/SoftBox";
import SoftTypography from "components/SoftTypography";
import PropTypes from "prop-types";
import Grid from "@mui/material/Grid";
import { styled } from "@mui/material/styles";
import InputLabel from "@mui/material/InputLabel";
import Snackbar from "@mui/material/Snackbar";
import Alert from "@mui/material/Alert";
import { useAuth } from "context/AuthContext";

// Responsive text field
const CustomTextField = styled(TextField)(({ theme }) => ({
  width: "100%",
  [theme.breakpoints.up("md")]: { width: "65%" },
  [theme.breakpoints.up("lg")]: { width: "45%" },
}));

function PersonalInfoCard({ title, description }) {
  const { user, updateMerchantPersonal } = useAuth();

  // const { businessPhone } = JSON.parse(user.merchantProfile?.businessProfile);

  const [firstName, setFirstName] = useState("");
  const [lastName, setLastName] = useState("");
  const [phone, setPhone] = useState("");

  const [loading, setLoading] = useState(false);
  const [snackbar, setSnackbar] = useState({
    open: false,
    message: "",
    severity: "success",
  });

  // Initialize values from user object
  useEffect(() => {
    if (user) {
      setFirstName(user.firstName || "");
      setLastName(user.lastName || "");
      setPhone(user?.phone);
    }
  }, [user]);

  // Submit handler
  const handleSubmit = async (event) => {
    event.preventDefault();

    if (!firstName || !lastName || !phone) {
      setSnackbar({
        open: true,
        message: "Please fill in all required fields",
        severity: "error",
      });
      return;
    }

    setLoading(true);

    try {
      const payload = {
        firstName,
        lastName,
        phoneNumber: phone,
      };

      await updateMerchantPersonal(payload);

      setSnackbar({
        open: true,
        message: "Personal information updated successfully!",
        severity: "success",
      });
    } catch (error) {
      setSnackbar({
        open: true,
        message:
          error?.response?.data?.message ||
          "Failed to update personal information",
        severity: "error",
      });
    } finally {
      setLoading(false);
    }
  };

  const handleCloseSnackbar = () =>
    setSnackbar((prev) => ({ ...prev, open: false }));

  return (
    <Card sx={{ height: "100%" }}>
      <SoftBox
        display='flex'
        justifyContent='space-between'
        alignItems='center'
        pt={2}
        px={2}
      >
        <SoftTypography
          variant='h6'
          fontWeight='medium'
          textTransform='capitalize'
        >
          {title}
        </SoftTypography>
      </SoftBox>

      <SoftBox p={2}>
        <SoftTypography variant='button' color='text' fontWeight='regular'>
          {description}
        </SoftTypography>

        <SoftBox opacity={0.3} mt={1} mb={2}>
          <Divider />
        </SoftBox>

        <form onSubmit={handleSubmit}>
          <Grid container spacing={2}>
            <Grid item xs={12}>
              <InputLabel style={{ fontSize: "15px" }}>First Name</InputLabel>
              <CustomTextField
                variant='outlined'
                value={firstName}
                onChange={(e) => setFirstName(e.target.value)}
                disabled={true}
              />
            </Grid>

            <Grid item xs={12}>
              <InputLabel style={{ fontSize: "15px" }}>Last Name</InputLabel>
              <CustomTextField
                variant='outlined'
                value={lastName}
                onChange={(e) => setLastName(e.target.value)}
                disabled={true}
              />
            </Grid>

            <Grid item xs={12}>
              <InputLabel style={{ fontSize: "15px" }}>Phone Number</InputLabel>
              <CustomTextField
                type='number'
                variant='outlined'
                value={phone}
                onChange={(e) => setPhone(e.target.value)}
                disabled={true}
              />
            </Grid>
          </Grid>

          <Button
            type='submit'
            variant='contained'
            color='primary'
            disabled={true}
            sx={{ mt: 2, color: "white" }}
          >
            {loading ? "Saving..." : "Save Changes"}
          </Button>
        </form>
      </SoftBox>

      <Snackbar
        open={snackbar.open}
        autoHideDuration={6000}
        onClose={handleCloseSnackbar}
        anchorOrigin={{ vertical: "top", horizontal: "right" }}
      >
        <Alert severity={snackbar.severity} onClose={handleCloseSnackbar}>
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Card>
  );
}

PersonalInfoCard.propTypes = {
  title: PropTypes.string.isRequired,
  description: PropTypes.string.isRequired,
};

export default PersonalInfoCard;
